import React from "react"
import { type Theme, css, type Interpolation } from "@emotion/react"
import { FloatingPortalRoot } from "@myvp/shared/src/components/floating/portal"
import useMergeRefs from "@myvp/shared/src/hooks/use-merge-refs"
import { KEYS } from "@myvp/shared/src/constants"
import useRefCallback from "@myvp/shared/src/hooks/use-ref-callback"

const baseOverflowCss = css`
  width: 100vw;
  max-width: 100vw;
  height: 100svh;
  max-height: 100svh;
  margin: 0;
  padding: 0;
  border: none;
  overflow: auto;
  background: transparent;
  ::backdrop {
    background-color: rgba(0, 0, 0, 0.5);
  }
`
const baseGridCss = css`
  display: inline-grid;
  padding: 24px;
  grid-template-rows: 1fr;
  grid-template-columns: 1fr;
  place-items: center;
  min-width: 100%;
  min-height: 100%;
`
const baseDialogCss = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid ${theme.palette.border.neutral.default};
  background: ${theme.palette.surface.neutral.default};
  box-shadow: 0px 12px 48px 0px rgba(0, 0, 0, 0.15);
  color: ${theme.palette.content.neutral.default};
`
const floatingRootCss = css`
  position: absolute;
  z-index: 1000;
`

const BaseDialog = (
  allProps: React.ComponentProps<"dialog"> & {
    onOpen?: () => void
    visible: boolean
    gridCss?: Interpolation<Theme>
    dialogCss?: Interpolation<Theme>
    overflowCss?: Interpolation<Theme>
    ignoreEscapeKey?: boolean
  }
) => {
  const {
    dialogCss,
    gridCss,
    overflowCss,
    visible,
    ignoreEscapeKey,
    onOpen,
    ref, // don't send props.ref since we're using combinedRefs
    ...props
  } = allProps

  const refDialog = React.useRef<HTMLDialogElement>(null)
  const combinedRefs = useMergeRefs([refDialog, ref])

  React.useEffect(() => {
    if (!visible || !ignoreEscapeKey) {
      return
    }
    const abortController = new AbortController()
    // NOTE: event listener needs to be on document or window (or body?) to work on safari
    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key === KEYS.escape) {
          event.preventDefault()
        }
      },
      {
        // capture phase is needed to work correctly with select (`useDismiss` from floating ui probably stops propagation)
        capture: true,
        signal: abortController.signal,
      }
    )
    return () => {
      abortController.abort()
    }
  }, [visible, ignoreEscapeKey])

  const refOnOpen = useRefCallback(onOpen)

  React.useEffect(() => {
    const dialog = refDialog.current
    if (!dialog || visible === dialog.open) return

    if (visible) {
      dialog.showModal()
      refOnOpen.current?.()
    } else {
      dialog.close()
    }
  }, [visible])

  React.useEffect(() => {
    if (visible) {
      const prevOverflow = document.body.style.overflow
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = prevOverflow
      }
    }
  }, [visible])

  return (
    <dialog
      {...props}
      ref={combinedRefs}
      onClose={props.onClose}
      css={[baseOverflowCss, overflowCss]}
    >
      <FloatingPortalRoot css={floatingRootCss}>
        <div css={[baseGridCss, gridCss]}>
          <div css={[baseDialogCss, dialogCss]}>{allProps.children}</div>
        </div>
      </FloatingPortalRoot>
    </dialog>
  )
}

export default BaseDialog
